import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import { FeatureFlagService } from '@trimble-gcs/feature-flags';
import { TranslationService } from '@trimble-gcs/i18n';
import {
  ModusButtonModule,
  ModusIconModule,
  ModusSwitchModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import { filter, map, Subscription, switchMap, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  Classifier,
  ClassifierType,
  DEFAULT_CLASSIFIER_TYPE,
} from '../classifier/classifier.model';
import { ClassifierService } from '../classifier/classifier.service';
import { classifierTranslations } from '../classifier/classifier.translations';
import { DialogComponent } from '../dialog/dialog.component';
import { DialogData } from '../dialog/dialog.model';
import { DialogService } from '../dialog/dialog.service';
import { FeatureFlagKey } from '../feature-flags/feature-flag-key';
import { FilePicker, PickerType } from './file-pickers/file-picker.models';
import { FilePickerService } from './file-pickers/file-picker.service';
import { LocalFilePickerComponent } from './file-pickers/local/local-file-picker.component';
import { importDialogTranslations } from './import-dialog.translations';
import { ImportFileListComponent } from './import-file-list/import-file-list.component';
import { ImportTimeRemainingComponent } from './import-time-remaining/import-time-remaining.component';
import {
  AddToImportScans,
  ResetImportScans,
  SetClassifierType,
  SetCombineToSingleScan,
  SetRunClassifier,
} from './import.actions';
import {
  ImportFile,
  ImportFileStatus,
  ImportScan,
  ImportScanError,
  ImportScanStatus,
  ImportStatus,
  MAX_IMPORT_FILES,
} from './import.models';
import { ImportService } from './import.service';
import { ImportState } from './import.state';

export const importDialogDefaultConfig: MatDialogConfig = {
  disableClose: true,
  width: '70%',
  minWidth: '500px',
  maxWidth: '650px',
};

export type ImportDialogResult = {
  reloadScans: boolean;
};

@UntilDestroy()
@Component({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusTooltipModule,
    ModusIconModule,
    ModusButtonModule,
    ModusSwitchModule,
    MatDialogModule,
    MatIconModule,
    MatRadioModule,
    LocalFilePickerComponent,
    ImportFileListComponent,
    ImportTimeRemainingComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './import-dialog.component.html',
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportDialogComponent implements OnDestroy {
  private readonly dialogService = inject(DialogService);
  private readonly importService = inject(ImportService);
  private readonly store = inject(Store);
  private readonly filePickerService = inject(FilePickerService);
  private readonly translationService = inject(TranslationService);
  private readonly classifierService = inject(ClassifierService);
  private readonly featureFlagService =
    inject<FeatureFlagService<FeatureFlagKey>>(FeatureFlagService);

  readonly translations = { ...importDialogTranslations(), ...classifierTranslations() };

  private readonly selectedFiles = signal<ImportFile[]>([]);
  readonly importScans = this.store.selectSignal(ImportState.importScans);
  readonly importFiles = this.getImportFiles();

  readonly status = ImportStatus;
  readonly importStatus = this.store.selectSignal(ImportState.status);
  readonly importStarted = computed(() => this.importStatus() !== ImportStatus.NotStarted);
  readonly disableImport = computed(
    () => this.selectedFiles().length === 0 || this.importStarted() || this.hasFilesWithErrors(),
  );

  readonly maxFilesMessage = computed(() => {
    const text = this.translations.status.maxFilesWarning.text;
    return this.translationService.translate(text, { maxFiles: MAX_IMPORT_FILES });
  });

  readonly maxFilesReached = computed(() => this.selectedFiles().length === MAX_IMPORT_FILES);
  readonly hasFilesWithErrors = computed(() =>
    this.importFiles().some((file) => file.status === ImportFileStatus.Error),
  );

  private cancelDialogRef: MatDialogRef<DialogComponent, boolean> | undefined;
  private filePickerSubscription?: Subscription;

  readonly disableMinimize = computed(() => this.importStatus() !== ImportStatus.Busy);

  readonly disableCancel = signal(false);
  readonly cancelButtonText = computed(() =>
    this.importStatus() === ImportStatus.Error || this.importStatus() === ImportStatus.Completed
      ? this.translations.close.button.text
      : this.translations.cancel.button.text,
  );

  private combineInitialValue = this.store.selectSnapshot(ImportState.combineToSingleScan);
  readonly combineControl = new FormControl<boolean>(this.combineInitialValue, {
    nonNullable: true,
  });
  readonly combineControlValue = toSignal(this.combineControl.valueChanges, {
    initialValue: this.combineInitialValue,
  });
  readonly combineControlEnabled = toSignal(
    this.combineControl.statusChanges.pipe(map(() => this.combineControl.enabled)),
    { initialValue: false },
  );
  readonly combineToSingleScan = computed(
    () => this.combineControlEnabled() && this.combineControlValue(),
  );

  private readonly pickers = toSignal(
    this.filePickerService
      .getFilePickers()
      .pipe(map((pickers) => pickers.filter((picker) => picker.enabled))),
    { initialValue: [] },
  );

  readonly enableLocalFilePicker = computed(() =>
    this.pickers().some((picker) => picker.pickerType === PickerType.Upload),
  );
  readonly webFilePickers = computed(() =>
    this.pickers()
      .filter((picker) => picker.pickerType !== PickerType.Upload)
      .map((picker) => {
        const tooltip = isDefined(picker.name)
          ? this.translationService.translate(this.translations.importFrom.tooltip.text, {
              source: picker.name,
            })
          : '';
        return { ...picker, tooltip };
      }),
  );
  readonly showWebFilePickers = computed(() => this.webFilePickers().length > 0);

  // Classifier
  readonly enableClassifier = toSignal(
    this.featureFlagService.hasFeature$('tools_import.classifier'),
    { initialValue: false },
  );

  // Run Classifier Toggle
  private readonly runClassifierInitialValue = this.store.selectSnapshot(ImportState.runClassifier);

  readonly runClassifierControl = new FormControl<boolean>(this.runClassifierInitialValue, {
    nonNullable: true,
  });

  private readonly runClassifierControlValue = toSignal(this.runClassifierControl.valueChanges, {
    initialValue: this.runClassifierInitialValue,
  });

  // Classifier Type Radio Group
  readonly classifierType = ClassifierType;

  readonly classifierTerrestrialLabel =
    this.translations.classifiers[ClassifierType.Terrestrial].text;

  readonly classifierArialLabel = this.translations.classifiers[ClassifierType.Aerial].text;

  private readonly classifiers = toSignal(this.classifierService.getClassifiers(), {
    initialValue: [],
  });

  readonly showClassierTypes = computed(
    () => this.enableClassifier() && this.runClassifierControlValue(),
  );

  private readonly classifierTypeInitialValue = this.store.selectSnapshot(
    ImportState.classifierType,
  );

  readonly classifierTypeControl = new FormControl<ClassifierType>(
    this.classifierTypeInitialValue,
    { nonNullable: true },
  );

  private readonly classifierTypeControlValue = toSignal(
    this.classifierTypeControl.valueChanges.pipe(
      map((value) => this.classifiers().find((classifier) => classifier.name === value)),
    ),
  );

  private readonly useClassifier = computed(() =>
    this.enableClassifier() && this.runClassifierControlValue()
      ? this.classifierTypeControlValue()
      : undefined,
  );

  constructor(private dialogRef: MatDialogRef<ImportDialogComponent, ImportDialogResult>) {
    this.createCombineInputEffect();
    this.createClassifierTypeEffect();
  }

  ngOnDestroy(): void {
    this.cancelDialogRef?.close();
    this.filePickerSubscription?.unsubscribe();
  }

  filePickerClick(filePicker: FilePicker) {
    this.filePickerSubscription?.unsubscribe();

    this.filePickerSubscription = this.filePickerService
      .openFilePicker(filePicker.pickerType)
      .subscribe((pickedFiles) => this.addFiles(pickedFiles));
  }

  addFiles(addFiles: ImportFile[]) {
    const selectedFiles = this.selectedFiles().filter((file) => {
      // Remove files already selected (to avoid duplicates) and add only the latest picked.
      return isNil(addFiles.find((addFile) => file.id === addFile.id));
    });

    const files = [...selectedFiles, ...addFiles];
    const limitedFiles = files.length > MAX_IMPORT_FILES ? files.slice(0, MAX_IMPORT_FILES) : files;

    this.selectedFiles.set(limitedFiles);
  }

  removeFiles(removeFiles: ImportFile[]) {
    const selectedFiles = this.selectedFiles();
    const filteredFiles = selectedFiles.filter((file) =>
      removeFiles.find((removeFile) => file.id !== removeFile.id),
    );
    this.selectedFiles.set(filteredFiles);
  }

  minimizeClick() {
    this.dialogRef.close({ reloadScans: false });
  }

  cancelClick() {
    const importStatus = this.importStatus();
    const showPrompt =
      (this.selectedFiles().length > 0 && importStatus === ImportStatus.NotStarted) ||
      importStatus === ImportStatus.Busy;

    const reloadScans = importStatus !== ImportStatus.NotStarted;

    if (!showPrompt) {
      this.store.dispatch(ResetImportScans);
      this.dialogRef.close({ reloadScans });
      return;
    }

    this.confirmCancel()
      .pipe(
        tap((cancel) => this.disableCancel.set(cancel)),
        filter((cancel) => cancel),
        switchMap(() => this.importService.cancelAllImports()),
      )
      .subscribe(() => {
        this.dialogRef.close({ reloadScans });
      });
  }

  importClick() {
    const importScans = this.mapToImportScans(
      this.selectedFiles(),
      this.combineToSingleScan(),
      this.useClassifier(),
    );

    this.store.dispatch([
      new SetCombineToSingleScan(this.combineControlValue()),
      new SetRunClassifier(this.runClassifierControlValue()),
      new SetClassifierType(this.classifierTypeControlValue()?.name ?? DEFAULT_CLASSIFIER_TYPE),
      new AddToImportScans(importScans),
    ]);
  }

  private getImportFiles() {
    return computed(() => {
      const importFiles =
        this.importStatus() === ImportStatus.NotStarted
          ? this.validateSelectedFiles(this.selectedFiles(), this.combineToSingleScan())
          : this.importScans().flatMap((importScan) => {
              return importScan.files.map((importFile) => {
                return this.mapImportScanStatusToImportFile(importScan, importFile);
              });
            });

      return importFiles;
    });
  }

  private validateSelectedFiles(files: ImportFile[], combineToSingleScan: boolean): ImportFile[] {
    return files.map((file) => {
      if (file.alreadyIngested && (files.length === 1 || !combineToSingleScan)) {
        return {
          ...file,
          status: ImportFileStatus.Error,
          statusMessage: this.translations.status.fileAlreadyImportedError.text,
        };
      }

      return file;
    });
  }

  private mapImportScanStatusToImportFile(
    importScan: ImportScan,
    importFile: ImportFile,
  ): ImportFile {
    /**
     * TODO: Remove this method once we display the ImportScan.
     * This method exists to map the ImportScan status onto an importFile.
     */
    switch (importScan.status) {
      case ImportScanStatus.CheckQuota:
      case ImportScanStatus.CreatePointcloud:
      case ImportScanStatus.CreateImport:
      case ImportScanStatus.CreateFiles:
        const importFilePrepare = importScan.files[0];
        if (importFilePrepare.id !== importFile.id) return importFile;

        return {
          ...importFilePrepare,
          status: ImportFileStatus.Busy,
          statusMessage: this.getStatusMessage(importScan.status),
        };

      case ImportScanStatus.UploadFiles:
        const isLastFile = importScan.files.at(-1)?.id === importFile.id;
        if (!isLastFile && importFile.status === ImportFileStatus.Completed) return importFile;

        return {
          ...importFile,
          status: ImportFileStatus.Busy,
          statusMessage: this.getStatusMessage(importScan.status),
        };

      case ImportScanStatus.Ingest:
        const importFileIngest = importScan.files.at(-1)!;
        if (importFileIngest.id !== importFile.id) return importFile;

        return {
          ...importFileIngest,
          status: ImportFileStatus.Busy,
          statusMessage: this.getStatusMessage(importScan.status),
        };

      case ImportScanStatus.Error:
        const importFileError =
          importScan.files.find((file) => file.status === ImportFileStatus.Error) ??
          importScan.files.find((file) => file.status === ImportFileStatus.Skipped) ??
          importScan.files.at(-1)!;
        if (importFileError.id !== importFile.id) return importFile;

        return {
          ...importFileError,
          status: ImportFileStatus.Error,
          statusMessage: isDefined(importScan.error)
            ? this.getErrorMessage(importScan.error)
            : undefined,
        };

      default:
        return importFile;
    }
  }

  private getStatusMessage(importScanStatus: ImportScanStatus) {
    switch (importScanStatus) {
      case ImportScanStatus.CheckQuota:
        return this.translations.fileStatus.checkQuota.text;
      case ImportScanStatus.CreatePointcloud:
        return this.translations.fileStatus.createPointcloud.text;
      case ImportScanStatus.CreateImport:
        return this.translations.fileStatus.createImport.text;
      case ImportScanStatus.CreateFiles:
        return this.translations.fileStatus.createFiles.text;
      case ImportScanStatus.UploadFiles:
        return this.translations.fileStatus.uploadFiles.text;
      case ImportScanStatus.Ingest:
        return this.translations.fileStatus.ingest.text;
      default:
        return '';
    }
  }

  private getErrorMessage(importScanError: ImportScanError) {
    switch (importScanError) {
      case ImportScanError.CheckQuota:
        return this.translations.fileError.checkQuota.text;
      case ImportScanError.QuotaExceeded:
        return this.translations.fileError.quotaExceeded.text;
      case ImportScanError.CreatePointcloud:
        return this.translations.fileError.createPointcloud.text;
      case ImportScanError.CreateImport:
        return this.translations.fileError.createImport.text;
      case ImportScanError.CreateFiles:
        return this.translations.fileError.createFiles.text;
      case ImportScanError.UploadFiles:
        return this.translations.fileError.uploadFiles.text;
      case ImportScanError.Ingest:
        return this.translations.fileError.ingest.text;
      default:
        return '';
    }
  }

  private mapToImportScans(
    selectedFiles: ImportFile[],
    combineToSingleScan: boolean,
    classifier?: Classifier,
  ): ImportScan[] {
    return combineToSingleScan
      ? [this.mapToImportScan(selectedFiles, classifier)]
      : selectedFiles.map((selectedFile) => this.mapToImportScan([selectedFile], classifier));
  }

  private mapToImportScan(selectedFiles: ImportFile[], classifier?: Classifier): ImportScan {
    const trimFileName = (filename: string, maxLength: number) =>
      filename.length > maxLength ? `${filename.substring(0, maxLength)}...` : filename;

    const name =
      selectedFiles.length === 1
        ? selectedFiles[0].name
        : trimFileName(selectedFiles.map((file) => file.name).join(' '), 200);

    return {
      id: uuidv4(),
      name,
      classifier,
      status: ImportScanStatus.Initialized,
      files: selectedFiles.map((selectedFile) => ({
        ...selectedFile,
        status: ImportFileStatus.Pending,
      })),
    };
  }

  private confirmCancel() {
    const dialogData = new DialogData(
      this.translations.cancel.confirm.title.text,
      this.translations.cancel.confirm.message.text,
      { text: this.translations.cancel.confirm.yes.text, color: 'danger' },
      { text: this.translations.cancel.confirm.no.text, color: 'secondary' },
    );

    this.cancelDialogRef = this.dialogService.showMessage(dialogData);

    return this.cancelDialogRef.afterClosed().pipe(map((confirmed) => confirmed ?? false));
  }

  private createCombineInputEffect() {
    effect(() => {
      if (this.importStatus() !== ImportStatus.NotStarted) {
        this.combineControl.disable();
        return;
      }

      const selectedFiles = this.selectedFiles().filter(
        (file) => file.status !== ImportFileStatus.Error,
      );

      // Cannot combine files when uploading & importing, because it's not supported by the API.
      const hasUpload = selectedFiles.some((file) => file.pickerType === PickerType.Upload);
      const hasImport = selectedFiles.some((file) => file.pickerType !== PickerType.Upload);

      if (selectedFiles.length <= 1 || (hasUpload && hasImport)) {
        this.combineControl.disable();
        return;
      }

      this.combineControl.enable();
    });
  }

  private createClassifierTypeEffect() {
    effect(() => {
      const selectedFiles = this.selectedFiles().filter(
        (file) => file.status !== ImportFileStatus.Error,
      );

      if (selectedFiles.length === 0 || this.importStatus() !== ImportStatus.NotStarted) {
        this.runClassifierControl.disable();
        this.classifierTypeControl.disable();
        return;
      }

      this.runClassifierControl.enable();

      if (this.runClassifierControlValue()) {
        this.classifierTypeControl.enable();
      } else {
        this.classifierTypeControl.disable();
      }
    });
  }
}
